/** A complete crossing shared by player placement and generated river villages. */
import { BRIDGE_DEF } from '../data/buildings.ts';
import type { TileMap } from './map.ts';
import { RESOURCE_KEYS, Terrain, WallKind, type ResourceBundle } from './types.ts';
import { clearNodesInRect, type ResourceNodeStore } from './systems/nodes.ts';

export type BridgeTile = [number, number];
export interface BridgePlan {
  valid: boolean;
  reason: string;
  axis: 'x' | 'y';
  tiles: BridgeTile[];
  approaches: BridgeTile[];
  banks: [BridgeTile, BridgeTile] | null;
  cost: Partial<ResourceBundle>;
}
export const MAX_BRIDGE_SPAN = 40;
const wet = (t: number) => t === Terrain.River || t === Terrain.Deep || t === Terrain.Bridge || t === Terrain.Ditch;
const empty = (reason: string): BridgePlan => ({valid:false,reason,axis:'x',tiles:[],approaches:[],banks:null,cost:{}});

function openGround(map: TileMap, x: number, y: number, landing = false): boolean {
  if (!map.inBounds(x,y)) return false;
  const k=map.idx(x,y), wall=map.wall[k];
  if (map.building[k]>=0 || map.rubble[k] || map.resourceSolid?.[k]) return false;
  if (wall!==WallKind.None && !(landing && wall===WallKind.Gate)) return false;
  return !landing || (!wet(map.terrain[k]) && map.terrain[k]!==Terrain.Ditch && map.terrain[k]!==Terrain.Marsh);
}

function crossing(map: TileMap, x: number, y: number, axis: 'x'|'y'): BridgePlan | null {
  const at=(along:number,lane:number):BridgeTile => axis==='x' ? [x+along,y+lane] : [x+lane,y+along];
  const water=(along:number,lane:number) => {
    const [tx,ty]=at(along,lane);
    return map.inBounds(tx,ty) && wet(map.terrain[map.idx(tx,ty)]);
  };
  if (!water(0,0) || !water(0,1)) return null;
  const ends:Array<[number,number]>=[];
  for(let lane=0;lane<2;lane++) {
    let lo=0,hi=0;
    while(lo>=-MAX_BRIDGE_SPAN && water(lo,lane))lo--;
    while(hi<=MAX_BRIDGE_SPAN && water(hi,lane))hi++;
    ends.push([lo,hi]);
  }
  const lo=Math.min(...ends.map(e=>e[0])),hi=Math.max(...ends.map(e=>e[1]));
  if(hi-lo-1>MAX_BRIDGE_SPAN)return null;
  // Bank edges may slope by a tile or two; a short supported overhang keeps
  // the deck straight. Avoid long land viaducts or bridging along a river.
  if(Math.abs(ends[0][0]-ends[1][0])>2 || Math.abs(ends[0][1]-ends[1][1])>2)return null;
  const plan:BridgePlan={valid:true,reason:'',axis,tiles:[],approaches:[],banks:[at(lo,0),at(hi,0)],cost:{}};
  for(let lane=0;lane<2;lane++) {
    for(let a=lo+1;a<hi;a++) {
      const tile=at(a,lane);plan.tiles.push(tile);
      if(!openGround(map,...tile)) {plan.valid=false;plan.reason='The crossing is blocked by a structure or deposit.';}
    }
    for(const a of [lo-1,lo,hi,hi+1]) {
      const tile=at(a,lane);plan.approaches.push(tile);
      if(!openGround(map,...tile,true)) {plan.valid=false;plan.reason='Both ends need two clear, dry landing tiles.';}
    }
  }
  const fresh=plan.tiles.filter(([tx,ty])=>map.terrain[map.idx(tx,ty)]!==Terrain.Bridge).length;
  for(const key of RESOURCE_KEYS)if(BRIDGE_DEF.cost[key])plan.cost[key]=BRIDGE_DEF.cost[key]!*fresh;
  return plan;
}

/** Click a channel or drag towards the other bank. Never returns a partial deck. */
export function planBridge(map: TileMap, x0: number, y0: number, x1=x0, y1=y0): BridgePlan {
  if(![x0,y0,x1,y1].every(Number.isFinite))return empty('Choose a river crossing.');
  x0=Math.floor(x0);y0=Math.floor(y0);x1=Math.floor(x1);y1=Math.floor(y1);
  if(!map.inBounds(x0,y0)||!map.inBounds(x1,y1))return empty('Keep both ends on the map.');
  const dx=x1-x0,dy=y1-y0,n=Math.max(Math.abs(dx),Math.abs(dy));
  const axes:Array<'x'|'y'>=n>=2 ? [Math.abs(dx)>=Math.abs(dy)?'x':'y'] : ['x','y'];
  const seeds:BridgeTile[]=[],seen=new Set<number>();
  const seed=(x:number,y:number)=>{
    if(!map.inBounds(x,y))return;
    const k=map.idx(x,y);
    if(seen.has(k)||!wet(map.terrain[k]))return;
    seen.add(k);seeds.push([x,y]);
  };
  if(n>=2)for(let i=0;i<=n;i++)seed(Math.round(x0+dx*i/n),Math.round(y0+dy*i/n));
  else for(let y=y0-3;y<=y0+3;y++)for(let x=x0-3;x<=x0+3;x++)seed(x,y);
  const mx=(x0+x1)/2,my=(y0+y1)/2;
  seeds.sort((a,b)=>(a[0]-mx)**2+(a[1]-my)**2-(b[0]-mx)**2-(b[1]-my)**2);
  let best:BridgePlan|null=null,blocked:BridgePlan|null=null,score=Infinity;
  for(const [x,y] of seeds.slice(0,12))for(const axis of axes)for(const shift of [0,-1,1,-2,2]) {
    const sx=x+(axis==='y'?shift:0),sy=y+(axis==='x'?shift:0),p=crossing(map,sx,sy,axis);
    if(!p)continue;
    if(!p.valid) {blocked??=p;continue;}
    const s=p.tiles.length+(sx-mx)**2*.12+(sy-my)**2*.12;
    if(s<score){best=p;score=s;}
  }
  return best??blocked??empty(seeds.length ? `No complete crossing here. Find clear banks within ${MAX_BRIDGE_SPAN} tiles.` : 'Click the river or drag from one bank towards the other.');
}

/** Apply an already validated plan; pricing and world dirty flags belong to the caller. */
export function paintBridgePlan(map: TileMap, nodes: ResourceNodeStore, plan: BridgePlan): number {
  if(!plan.valid)return 0;
  let changed=0;
  for(const [x,y] of plan.tiles) {
    const k=map.idx(x,y);
    clearNodesInRect(nodes,x,y,x,y);
    if(map.terrain[k]!==Terrain.Bridge)changed++;
    map.terrain[k]=Terrain.Bridge;
  }
  for(const [x,y] of plan.approaches) {
    const k=map.idx(x,y);clearNodesInRect(nodes,x,y,x,y);
    if(map.terrain[k]!==Terrain.Road)changed++;
    map.terrain[k]=Terrain.Road;
  }
  if(changed)map.terrainVersion++;
  return changed;
}
